import React, { useMemo } from 'react';

/**
 * Minimal Markdown renderer for admin-authored blog bodies.
 *
 * Deliberately builds React elements instead of setting innerHTML — post content
 * comes out of the database, and rendering it as raw HTML would turn the admin
 * editor into a stored-XSS surface for every reader of the public site.
 *
 * Supported: # ## ### headings, > quotes, - / * bullets, 1. numbered lists,
 * ``` fenced code, --- rules, ![alt](src) images, and inline **bold**, *italic*,
 * `code` and [links](url).
 */

const INLINE_RE = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`|\[[^\]\n]+\]\([^)\s]+\))/g;

function renderInline(text, keyPrefix) {
  if (!text) return null;
  const parts = String(text).split(INLINE_RE).filter(Boolean);

  return parts.map((part, i) => {
    const key = `${keyPrefix}-i${i}`;

    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={key} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={key}>{part.slice(1, -1)}</em>;
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code key={key} className="px-1.5 py-0.5 rounded-md bg-muted text-foreground text-[0.9em] font-mono">
          {part.slice(1, -1)}
        </code>
      );
    }

    const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      const [, label, href] = link;
      const external = /^https?:\/\//i.test(href);
      return (
        <a
          key={key}
          href={href}
          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          className="text-primary font-medium underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors"
        >
          {label}
        </a>
      );
    }

    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

/** Group raw lines into block-level nodes before rendering. */
function parseBlocks(source) {
  // Tolerate literal "\n" sequences stored by older tooling alongside real newlines.
  const lines = String(source || '').replace(/\\n/g, '\n').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) { i++; continue; }

    // Fenced code block
    if (trimmed.startsWith('```')) {
      const lang = trimmed.slice(3).trim();
      const body = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) { body.push(lines[i]); i++; }
      i++; // closing fence
      blocks.push({ type: 'code', lang, text: body.join('\n') });
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) { blocks.push({ type: 'hr' }); i++; continue; }

    // Standalone image
    const img = trimmed.match(/^!\[([^\]]*)\]\(([^)\s]+)\)$/);
    if (img) { blocks.push({ type: 'image', alt: img[1], src: img[2] }); i++; continue; }

    // Headings
    const heading = trimmed.match(/^(#{1,4})\s+(.*)$/);
    if (heading) { blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] }); i++; continue; }

    // Blockquote (consecutive > lines collapse into one quote)
    if (trimmed.startsWith('>')) {
      const body = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        body.push(lines[i].trim().replace(/^>\s?/, ''));
        i++;
      }
      blocks.push({ type: 'quote', text: body.join(' ') });
      continue;
    }

    // Bullet list
    if (/^[-*+]\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^[-*+]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*+]\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    // Numbered list
    if (/^\d+[.)]\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\d+[.)]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+[.)]\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    // Paragraph — soft-wrapped lines join until a blank line or a new block starts.
    const para = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^(#{1,4}\s|>|[-*+]\s|\d+[.)]\s|```|!\[)/.test(lines[i].trim()) &&
      !/^(-{3,}|\*{3,}|_{3,})$/.test(lines[i].trim())
    ) {
      para.push(lines[i].trim());
      i++;
    }
    if (para.length) blocks.push({ type: 'p', text: para.join(' ') });
    else i++; // safety valve — never spin on an unconsumed line
  }

  return blocks;
}

export default function MarkdownContent({ content, className = '' }) {
  const blocks = useMemo(() => parseBlocks(content), [content]);

  if (!blocks.length) return null;

  return (
    <div className={`text-[15px] sm:text-base leading-[1.85] text-muted-foreground ${className}`}>
      {blocks.map((b, idx) => {
        const key = `b${idx}`;

        switch (b.type) {
          case 'heading': {
            const sizes = {
              1: 'text-2xl sm:text-3xl mt-10 mb-4',
              2: 'text-xl sm:text-2xl mt-10 mb-3',
              3: 'text-lg sm:text-xl mt-8 mb-2',
              4: 'text-base sm:text-lg mt-6 mb-2',
            };
            const Tag = `h${Math.min(b.level + 1, 6)}`;
            return (
              <Tag key={key} className={`font-heading font-bold text-foreground scroll-mt-24 ${sizes[b.level] || sizes[3]}`}>
                {renderInline(b.text, key)}
              </Tag>
            );
          }

          case 'quote':
            return (
              <blockquote key={key} className="my-6 border-l-4 border-primary bg-primary/5 rounded-r-xl px-5 py-4 text-foreground/90 italic">
                {renderInline(b.text, key)}
              </blockquote>
            );

          case 'ul':
            return (
              <ul key={key} className="my-5 space-y-2 pl-1">
                {b.items.map((it, j) => (
                  <li key={`${key}-${j}`} className="flex gap-3">
                    <span className="mt-[0.6em] w-1.5 h-1.5 rounded-full bg-primary shrink-0" aria-hidden />
                    <span>{renderInline(it, `${key}-${j}`)}</span>
                  </li>
                ))}
              </ul>
            );

          case 'ol':
            return (
              <ol key={key} className="my-5 space-y-2">
                {b.items.map((it, j) => (
                  <li key={`${key}-${j}`} className="flex gap-3">
                    <span className="mt-0.5 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center shrink-0">
                      {j + 1}
                    </span>
                    <span>{renderInline(it, `${key}-${j}`)}</span>
                  </li>
                ))}
              </ol>
            );

          case 'code':
            return (
              <pre key={key} className="my-6 overflow-x-auto rounded-xl bg-foreground text-background p-4 text-[13px] leading-relaxed">
                <code className="font-mono">{b.text}</code>
              </pre>
            );

          case 'image':
            return (
              <figure key={key} className="my-7">
                <img src={b.src} alt={b.alt} loading="lazy" className="w-full rounded-xl border object-cover" />
                {b.alt && <figcaption className="text-xs text-center text-muted-foreground mt-2">{b.alt}</figcaption>}
              </figure>
            );

          case 'hr':
            return <hr key={key} className="my-9 border-border" />;

          default:
            return <p key={key} className="my-4">{renderInline(b.text, key)}</p>;
        }
      })}
    </div>
  );
}
